import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ThemeButton } from './ThemeWrapper';
import StudentDetailInlineEdit from './StudentList/StudentDetailInlineEdit';
import { reasonLabel } from '../utils/stagingReviewCategories';
import { redactPayloadPasswords } from '../utils/stagingStudentEditForm';
import { mapStagingDocToAdminForm } from '../utils/mapStagingDocToAdminForm';
import { loadStudentForAdminEdit } from '../utils/loadStudentForAdminEdit';
import { resolveStagingSqlStudentId } from '../utils/resolveStagingSqlStudentId';
import {
  buildStagingFieldGuidance,
  createStagingGetFieldStyle,
} from '../utils/stagingFieldGuidance';
import {
  findStagingEmailSiblings,
  resolveMultiRowGuidance,
} from '../utils/stagingMultiRowGuidance';
import { resolveCompletenessFocusTarget } from '../domain/studentCompleteness/completenessFocusTarget';
import StagingMultiRowActionBanner from './StagingMultiRowActionBanner';
import StagingDuplicateClusterPanel from './StagingDuplicateClusterPanel';
import { isInFileDuplicateDoc } from '../utils/stagingReviewCategories';

/**
 * Staging review side panel — same form + save behavior as /admin/edit-user when the
 * student already exists; staging decide creates brand-new students then runs the same update.
 */
export default function StagingStudentEditPanel({
  selected,
  acting,
  onApply,
  onSkip,
  onNext,
  hasNext,
  categoryPosition,
  showJson = false,
  onToggleJson,
  enrollmentDecision,
  onEnrollmentDecisionChange,
  queueItems = [],
  onSelectDocument,
  onClusterAction,
  focusFieldKey = null,
  duplicateCluster = null,
}) {
  const { user } = useContext(AuthContext);
  const token = user?.token || user?.user?.token;
  const goNextRef = useRef(false);

  const [hydrate, setHydrate] = useState({
    loading: false,
    formData: null,
    userId: null,
    studentId: null,
    source: 'staging', // 'sql' | 'staging'
    error: '',
  });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selected) {
        setHydrate({
          loading: false,
          formData: null,
          userId: null,
          studentId: null,
          source: 'staging',
          error: '',
        });
        return;
      }

      const rr = selected.ruleResult || {};
      const email = rr.email || selected.emailKey;
      let studentId = null;

      setHydrate((prev) => ({
        ...prev,
        loading: true,
        error: '',
        formData: null,
        userId: null,
        studentId: null,
      }));

      try {
        studentId = await resolveStagingSqlStudentId({
          matchedStudentId: rr.matchedStudentId,
          email,
        });

        if (studentId) {
          const loaded = await loadStudentForAdminEdit({ studentId, email });
          if (cancelled) return;
          // Overlay staging phone/name fixes when SQL row still incomplete
          const stagingOverlay = mapStagingDocToAdminForm(selected);
          const merged = {
            ...loaded.formData,
            // Prefer SQL, but fill blanks from staging import
            ...Object.fromEntries(
              Object.entries(stagingOverlay).filter(([key, val]) => {
                if (key === 'password' || key === 'role') return false;
                const current = loaded.formData?.[key];
                const empty =
                  current === undefined ||
                  current === null ||
                  (typeof current === 'string' && current.trim() === '');
                return empty && val !== undefined && val !== null && String(val).trim() !== '';
              })
            ),
            role: 'student',
          };
          setHydrate({
            loading: false,
            formData: merged,
            userId: loaded.userId,
            studentId: String(studentId),
            source: 'sql',
            error: '',
          });
          return;
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('SQL hydrate for staging edit failed, using import payload', err);
        }
      }

      if (cancelled) return;
      setHydrate({
        loading: false,
        formData: mapStagingDocToAdminForm(selected),
        userId: null,
        studentId: null,
        source: 'staging',
        error: '',
      });
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const focusTarget = useMemo(
    () => resolveCompletenessFocusTarget(focusFieldKey),
    [focusFieldKey]
  );

  const guidance = useMemo(
    () =>
      buildStagingFieldGuidance(hydrate.formData || {}, selected, {
        focusFormFields: focusTarget?.formFields || [],
      }),
    [hydrate.formData, selected, focusTarget]
  );

  const getFieldStyle = useMemo(() => createStagingGetFieldStyle(guidance), [guidance]);

  const editUserHref = useMemo(() => {
    if (!hydrate.studentId) return null;
    const email = selected?.ruleResult?.email || selected?.emailKey || '';
    const params = new URLSearchParams();
    if (email) params.set('email', email);
    params.set('studentId', hydrate.studentId);
    return `/admin/edit-user?${params.toString()}`;
  }, [hydrate.studentId, selected]);

  const siblings = useMemo(
    () => findStagingEmailSiblings(queueItems, selected),
    [queueItems, selected]
  );
  const multiRowGuidance = useMemo(
    () => resolveMultiRowGuidance(selected, siblings),
    [selected, siblings]
  );

  if (!selected) {
    return (
      <p className="theme-text-secondary">
        Expand a category on the left and select a student to open the Edit User form.
      </p>
    );
  }

  if (hydrate.loading || !hydrate.formData) {
    return (
      <p className="text-sm theme-text-secondary">Loading Edit User form…</p>
    );
  }

  const rr = selected.ruleResult || {};

  const guideSlot = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-semibold theme-text text-lg truncate">
            Edit student — {selected.emailKey || 'Untitled'}
          </h2>
          <p className="text-sm theme-text-secondary mt-1">
            Source: {selected.source} ·{' '}
            {selected.ingestBatch?.originalFilename || selected.ingestBatchId} · Match:{' '}
            {rr.matchStatus || '—'}
            {categoryPosition
              ? ` · ${categoryPosition.categoryLabel} (${categoryPosition.index}/${categoryPosition.total})`
              : ''}
            {focusTarget
              ? ` · Jump to: ${focusTarget.label}`
              : ''}
            {multiRowGuidance.emailOccurrenceCount > 1
              ? ` · ${multiRowGuidance.emailOccurrenceCount} rows same email`
              : ''}
          </p>
          <p className="text-xs mt-1">
            {hydrate.source === 'sql' ? (
              <span className="text-green-700">
                Loaded from SQL (same data as Edit User)
                {editUserHref ? (
                  <>
                    {' · '}
                    <Link className="underline font-medium" to={editUserHref}>
                      Open full Edit User page
                    </Link>
                  </>
                ) : null}
              </span>
            ) : (
              <span className="text-amber-800">
                New student — not in SQL yet. Save creates the account (Add/Edit User rules), then
                applies the full Edit User update.
              </span>
            )}
          </p>
        </div>
        <ThemeButton
          type="button"
          variant="secondary"
          disabled={!hasNext || acting}
          onClick={onNext}
        >
          Next in category
        </ThemeButton>
      </div>

      {isInFileDuplicateDoc(selected) ? (
        <StagingDuplicateClusterPanel
          email={duplicateCluster?.email || selected.emailKey || ''}
          rows={duplicateCluster?.rows || []}
          selectedId={selected.id}
          formBatchId={hydrate.formData?.batchId || ''}
          onSelectRow={onSelectDocument}
          onClusterAction={onClusterAction}
          acting={acting}
          loading={Boolean(duplicateCluster?.loading)}
        />
      ) : (
        <StagingMultiRowActionBanner
          guidance={multiRowGuidance}
          enrollmentDecision={enrollmentDecision}
          onEnrollmentDecisionChange={onEnrollmentDecisionChange}
          onSelectSibling={onSelectDocument}
          onClusterAction={onClusterAction}
          acting={acting}
        />
      )}

      <div className="p-3 rounded-lg border bg-[var(--staff-surface,#f8fafc)]">
        <h3 className="font-medium theme-text mb-1 text-sm">Why review?</h3>
        <ul className="list-disc pl-5 text-sm theme-text space-y-0.5">
          {(rr.reviewReasons || []).map((r) => (
            <li key={r}>{reasonLabel(r)}</li>
          ))}
          {(rr.conflicts || []).map((c) => (
            <li key={`c-${c}`} className="theme-error-text">
              Conflict: {c}
            </li>
          ))}
          {(rr.warnings || []).slice(0, 6).map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </div>

      {Array.isArray(rr.discrepancies) && rr.discrepancies.length > 0 ? (
        <div className="p-3 rounded-lg border border-amber-300 bg-amber-50/90 overflow-x-auto">
          <h3 className="font-semibold text-amber-900 mb-2 text-sm">
            Discrepancies vs database ({rr.discrepancies.length})
          </h3>
          <p className="text-xs text-amber-800 mb-2">
            Import values differ from the matched student. Review and Save to update SQL.
          </p>
          <table className="w-full text-xs theme-table">
            <thead>
              <tr>
                <th className="text-left p-1">Field</th>
                <th className="text-left p-1">In database</th>
                <th className="text-left p-1">In upload</th>
                <th className="text-left p-1">Change</th>
              </tr>
            </thead>
            <tbody>
              {rr.discrepancies.map((d) => (
                <tr key={`${d.field}-${d.importValue}`}>
                  <td className="p-1 font-medium">{d.label || d.field}</td>
                  <td className="p-1 text-red-800">{d.dbValue}</td>
                  <td className="p-1 text-green-800">{d.importValue}</td>
                  <td className="p-1 capitalize">{d.kind || 'changed'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : rr.matchStatus === 'existing' ? (
        <div className="p-3 rounded-lg border border-green-200 bg-green-50/80 text-sm text-green-900">
          Existing student matched by email
          {rr.matchedStudentId ? ` (ID ${rr.matchedStudentId})` : ''}. Flagged for update
          confirmation — no field differences detected in mapped columns.
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-red-200 bg-red-50/80 p-3">
          <h3 className="font-semibold text-red-800 mb-1">Needs attention</h3>
          {guidance.attention.length ? (
            <ul className="space-y-1">
              {guidance.attention.map((item) => (
                <li key={item.field} className="text-red-900">
                  <span className="font-medium">{item.label}</span>
                  <span className="text-red-700"> — missing</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-red-800/70 text-xs">No critical gaps in core fields.</p>
          )}
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50/80 p-3">
          <h3 className="font-semibold text-green-800 mb-1">Already filled</h3>
          {guidance.filled.length ? (
            <ul className="space-y-1 max-h-36 overflow-y-auto">
              {guidance.filled.slice(0, 12).map((item) => (
                <li key={item.field} className="text-green-900 truncate">
                  <span className="font-medium">{item.label}</span>
                  <span className="text-green-700"> — {item.value}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-green-800/70 text-xs">No core fields filled yet.</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs theme-text-secondary">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-red-100 border border-red-400" /> Needs
          attention
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-green-100 border border-green-400" />{' '}
          Filled OK
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-amber-50 border border-amber-300" />{' '}
          Optional empty
        </span>
      </div>

      <label className="text-sm theme-text block max-w-md">
        Enrollment action when clearing this staging row
        <select
          className="theme-input w-full mt-1 px-2 py-1.5 rounded"
          value={enrollmentDecision}
          onChange={(e) => onEnrollmentDecisionChange(e.target.value)}
        >
          <option value="create">Add enrollment (create if batch set)</option>
          <option value="update">Update existing enrollment</option>
          <option value="skip">Merge profile only — skip staging enrollment</option>
        </select>
      </label>

      <p className="text-xs theme-text-secondary">
        Form and save match{' '}
        <Link className="underline" to="/admin/edit-user">
          Edit User
        </Link>
        : required fields, batch auto-fill, placement, counselling, etc.
        {multiRowGuidance.show
          ? ' For duplicate rows: merge or add enrollment on one student; every ticked row’s batch is kept, then the extra Excel rows are skipped.'
          : ''}
      </p>
    </div>
  );

  const actions = (
    <div className="flex flex-wrap gap-2 pt-3 border-t sticky bottom-0 bg-[var(--staff-surface,var(--color-surface))] py-3 z-10">
      <ThemeButton
        type="submit"
        disabled={acting}
        onClick={() => {
          goNextRef.current = false;
        }}
      >
        {acting ? 'Saving…' : 'Save (Edit User)'}
      </ThemeButton>
      <ThemeButton
        type="button"
        variant="secondary"
        disabled={acting || !hasNext}
        onClick={() => {
          goNextRef.current = true;
          document.getElementById('staging-full-student-form')?.requestSubmit?.();
        }}
      >
        Save & next
      </ThemeButton>
      <ThemeButton type="button" variant="secondary" disabled={acting} onClick={onSkip}>
        Skip / discard
      </ThemeButton>
      <ThemeButton
        type="button"
        variant="secondary"
        disabled={acting || !hasNext}
        onClick={onNext}
      >
        Next without saving
      </ThemeButton>
    </div>
  );

  return (
    <div className="min-w-0">
      <StudentDetailInlineEdit
        token={token}
        preloadKey={`${selected.id}:${hydrate.source}:${hydrate.studentId || 'new'}:${focusFieldKey || ''}`}
        preloadFormData={hydrate.formData}
        initialUserId={hydrate.userId}
        getFieldStyle={getFieldStyle}
        focusTarget={focusTarget}
        guideSlot={guideSlot}
        actionSlot={actions}
        hideDefaultActions
        formClassName="space-y-4"
        formId="staging-full-student-form"
        saving={acting}
        onCancel={onNext}
        onSave={async (formData, meta) => {
          const goNext = goNextRef.current;
          goNextRef.current = false;
          await onApply(formData, {
            goNext,
            userId: meta?.userId || hydrate.userId,
            companyOptions: meta?.companyOptions,
            refreshCompanyOptions: meta?.refreshCompanyOptions,
          });
        }}
      />

      <div className="mt-4">
        <button
          type="button"
          className="text-xs underline theme-text-secondary"
          onClick={onToggleJson}
        >
          {showJson ? 'Hide' : 'Show'} staging payload / unmapped fields
        </button>
        {showJson && (
          <div className="mt-2 space-y-3">
            <div>
              <h3 className="font-medium theme-text mb-1 text-sm">Unmapped fields</h3>
              <pre className="staging-json-block text-xs p-3 rounded-lg border max-h-40 overflow-auto whitespace-pre-wrap break-words">
                {JSON.stringify(rr.unknownFields || {}, null, 2)}
              </pre>
            </div>
            <div>
              <h3 className="font-medium theme-text mb-1 text-sm">Raw payload</h3>
              <pre className="staging-json-block text-xs p-3 rounded-lg border max-h-56 overflow-auto whitespace-pre-wrap break-words">
                {JSON.stringify(redactPayloadPasswords(selected.payload), null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
